import { z } from 'zod';

export enum AttachmentType {
  PAYMENT_PROOF = 'PAYMENT_PROOF',
  SUPPORTING_DOCUMENT = 'SUPPORTING_DOCUMENT',
  RECEIPT = 'RECEIPT',
  INVOICE = 'INVOICE',
  CHAT_MESSAGE = 'CHAT_MESSAGE',
  OTHER = 'OTHER',
}

export const isGeneratedOperativeReportAttachment = (attachment: {
  type: AttachmentType;
  originalFilename: string;
  createdBy?: string | null;
}): boolean =>
  attachment.type === AttachmentType.SUPPORTING_DOCUMENT &&
  attachment.createdBy === null &&
  attachment.originalFilename.startsWith('operative-report-') &&
  attachment.originalFilename.endsWith('.pdf');

export const ATTACHMENT_CONTENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.ms-excel',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/tiff',
  'image/bmp',
  'image/heic',
  'image/heif',
] as const;

export const attachmentContentTypeSchema = z.enum(ATTACHMENT_CONTENT_TYPES);

export const attachmentSummarySchema = z
  .object({
    id: z.guid(),
    type: z.enum(Object.values(AttachmentType)),
    originalFilename: z.string().min(1),
    createdAt: z.string(),
    createdBy: z.guid().nullable(),
    createdByName: z.string().nullable().optional(),
    claimQueryID: z.guid().nullable().optional(),
    claimQueryStatus: z
      .enum(['CREATED', 'SUBMITTED', 'PENDING', 'COMPLETED', 'FAILED'])
      .nullable()
      .optional(),
  })
  .strict();

export const claimAttachmentSummarySchema = attachmentSummarySchema
  .extend({
    claimID: z.guid(),
  })
  .strict();

export const attachmentUploadFileSchema = z
  .object({
    originalFilename: z.string().trim().min(1),
    contentType: attachmentContentTypeSchema,
    sizeBytes: z.number().int().nonnegative(),
  })
  .strict();

export const attachmentUploadTargetSchema = attachmentUploadFileSchema
  .extend({
    id: z.guid(),
    uploadURL: z.url(),
  })
  .strict();

export const attachmentUploadSchema = attachmentUploadFileSchema
  .extend({ id: z.guid() })
  .strict();

export const CLAIM_ATTACHMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

export const claimAttachmentUploadFileSchema = attachmentUploadFileSchema
  .extend({
    sizeBytes: z.number().int().positive().max(CLAIM_ATTACHMENT_MAX_SIZE_BYTES),
  })
  .strict();

export const claimAttachmentUploadSchema = claimAttachmentUploadFileSchema
  .extend({ id: z.guid() })
  .strict();

export const prepareClaimAttachmentSchema = z
  .object({ claimID: z.guid(), file: claimAttachmentUploadFileSchema })
  .strict();

export const completeClaimAttachmentSchema = z
  .object({
    claimID: z.guid(),
    attachment: claimAttachmentUploadSchema,
  })
  .strict();

export const CLAIM_PAYMENT_ATTACHMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
] as const;

export const claimPaymentAttachmentContentTypeSchema = z.enum(
  CLAIM_PAYMENT_ATTACHMENT_CONTENT_TYPES
);

export const claimPaymentAttachmentUploadFileSchema = attachmentUploadFileSchema
  .extend({ contentType: claimPaymentAttachmentContentTypeSchema })
  .strict();

export const claimPaymentAttachmentUploadTargetSchema =
  attachmentUploadTargetSchema
    .extend({ contentType: claimPaymentAttachmentContentTypeSchema })
    .strict();

export const claimPaymentAttachmentSchema = attachmentUploadSchema
  .extend({ contentType: claimPaymentAttachmentContentTypeSchema })
  .strict();

export const prepareClaimPaymentAttachmentsSchema = z
  .object({
    claimID: z.guid(),
    files: z.array(claimPaymentAttachmentUploadFileSchema).min(1).max(5),
  })
  .strict();

export const SCI_ATTACHMENT_CONTENT_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'text/plain',
  'image/jpeg',
] as const;

export const SCI_REPORT_TYPES = [
  'Operative/Procedure Report',
  'Pathology Report',
  'Consultation Notes',
  'Chart Notes',
  'Other',
] as const;

export const SCI_ATTACHMENT_MAX_SIZE_BYTES = 5_000_000;
export const SCI_ATTACHMENT_MAX_FILENAME_LENGTH = 60;
export const DEFAULT_SCI_MESSAGE = 'Supporting document attached.';

export const sciAttachmentContentTypeSchema = z.enum(
  SCI_ATTACHMENT_CONTENT_TYPES
);

export const sciReportTypeSchema = z.enum(SCI_REPORT_TYPES);

export const sciFilenameExtensionsByContentType: Record<
  z.infer<typeof sciAttachmentContentTypeSchema>,
  readonly string[]
> = {
  'application/msword': ['doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    'docx',
  ],
  'application/pdf': ['pdf'],
  'text/plain': ['txt'],
  'image/jpeg': ['jpg', 'jpeg'],
};

export const getSCIContentTypeFromFilename = (
  filename: string
): z.infer<typeof sciAttachmentContentTypeSchema> | undefined => {
  const extension = filename.split('.').pop()?.toLowerCase();
  if (!extension) return undefined;
  return SCI_ATTACHMENT_CONTENT_TYPES.find((contentType) =>
    sciFilenameExtensionsByContentType[contentType].some(
      (supportedExtension) => supportedExtension === extension
    )
  );
};

export const isSCIEligibleExistingAttachment = (attachment: {
  originalFilename: string;
}): boolean =>
  attachment.originalFilename.trim().length <=
    SCI_ATTACHMENT_MAX_FILENAME_LENGTH &&
  getSCIContentTypeFromFilename(attachment.originalFilename) !== undefined;

export const sciAttachmentUploadFileSchema = attachmentUploadFileSchema
  .extend({
    originalFilename: z
      .string()
      .trim()
      .min(1)
      .max(SCI_ATTACHMENT_MAX_FILENAME_LENGTH),
    contentType: sciAttachmentContentTypeSchema,
    sizeBytes: z.number().int().positive().max(SCI_ATTACHMENT_MAX_SIZE_BYTES),
  })
  .strict()
  .superRefine((file, ctx) => {
    const extension = file.originalFilename.split('.').pop()?.toLowerCase();
    if (
      !extension ||
      !sciFilenameExtensionsByContentType[file.contentType].includes(extension)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['originalFilename'],
        message: 'Filename extension does not match the selected file type.',
      });
    }
  });

export const sciAttachmentUploadSchema = sciAttachmentUploadFileSchema
  .safeExtend({ id: z.guid() })
  .strict();

export const sciAttachmentUploadTargetSchema = sciAttachmentUploadSchema
  .safeExtend({
    uploadURL: z.url(),
    uploadHeaders: z.record(z.string(), z.string()),
  })
  .strict();

export const prepareSCIClaimAttachmentSchema = z
  .object({
    claimID: z.guid(),
    file: sciAttachmentUploadFileSchema,
  })
  .strict();

const submitSCIClaimQueryBaseSchema = z.object({
  claimID: z.guid(),
  reportType: sciReportTypeSchema,
  message: z.string().trim().min(1).max(250),
});

const submitUploadedSCIClaimQuerySchema = submitSCIClaimQueryBaseSchema
  .extend({
    attachment: sciAttachmentUploadSchema,
  })
  .strict();

const submitExistingSCIClaimQuerySchema = submitSCIClaimQueryBaseSchema
  .extend({
    attachmentID: z.guid(),
  })
  .strict();

export const submitSCIClaimQuerySchema = z.union([
  submitUploadedSCIClaimQuerySchema,
  submitExistingSCIClaimQuerySchema,
]);

export const listClaimAttachmentsSchema = z
  .object({
    claimID: z.guid(),
    sciEligible: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
  })
  .strict();

export const getAttachmentDownloadSchema = z
  .object({ attachmentID: z.guid() })
  .strict();

export type AttachmentSummary = z.infer<typeof attachmentSummarySchema>;
export type AttachmentContentType = z.infer<typeof attachmentContentTypeSchema>;
export type ClaimAttachmentSummary = z.infer<
  typeof claimAttachmentSummarySchema
>;
export type AttachmentUploadFile = z.infer<typeof attachmentUploadFileSchema>;
export type AttachmentUpload = z.infer<typeof attachmentUploadSchema>;
export type AttachmentUploadTarget = z.infer<
  typeof attachmentUploadTargetSchema
>;
export type PrepareClaimAttachmentInput = z.infer<
  typeof prepareClaimAttachmentSchema
>;
export type CompleteClaimAttachmentInput = z.infer<
  typeof completeClaimAttachmentSchema
>;
export type ClaimPaymentAttachment = z.infer<
  typeof claimPaymentAttachmentSchema
>;
export type ClaimPaymentAttachmentUploadTarget = z.infer<
  typeof claimPaymentAttachmentUploadTargetSchema
>;
export type PrepareClaimPaymentAttachmentsInput = z.infer<
  typeof prepareClaimPaymentAttachmentsSchema
>;
export type SCIAttachmentContentType = z.infer<
  typeof sciAttachmentContentTypeSchema
>;
export type SCIReportType = z.infer<typeof sciReportTypeSchema>;
export type SCIAttachmentUpload = z.infer<typeof sciAttachmentUploadSchema>;
export type SCIAttachmentUploadTarget = z.infer<
  typeof sciAttachmentUploadTargetSchema
>;
export type PrepareSCIClaimAttachmentInput = z.infer<
  typeof prepareSCIClaimAttachmentSchema
>;
export type SubmitSCIClaimQueryInput = z.infer<
  typeof submitSCIClaimQuerySchema
>;
